import { Controller } from "@hotwired/stimulus"

const escape = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
const tokenPattern = (name, flags = "u") => new RegExp(`(?<![\\p{L}\\p{N}_@])@${escape(name)}(?![\\p{L}\\p{N}_])`, flags)

// Inline @ suggestions and the toolbar picker share one panel. Text is still
// native textarea text; only explicitly picked identities carry mention data.
export default class extends Controller {
    static targets = ["body", "comment", "tags", "panel", "query", "results", "message", "tab", "searchButton"]
    static values = { url: String }

    connect() {
        this.field = this.bodyTarget
        this.kind = "people"
        this.sequence = 0
        this.outside = (event) => {
            if (!this.panelTarget.contains(event.target) && event.target !== this.field && !event.target.closest(".studio-mention-open")) this.close()
        }
        this.reposition = () => { if (this.isOpen) this.position() }
        document.addEventListener("pointerdown", this.outside)
        window.addEventListener("resize", this.reposition)
        window.addEventListener("scroll", this.reposition, true)
        this.syncTags()
    }

    disconnect() {
        this.close()
        document.removeEventListener("pointerdown", this.outside)
        window.removeEventListener("resize", this.reposition)
        window.removeEventListener("scroll", this.reposition, true)
    }

    get isOpen() { return this.panelTarget.matches(":popover-open") }
    get fields() { return [this.bodyTarget, ...(this.hasCommentTarget ? [this.commentTarget] : [])] }
    remember(event) { this.field = event.target }

    input(event) {
        this.field = event.target
        this.syncTags()
        this.detect()
    }

    caret(event) {
        if (event.type === "keyup" && ["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab"].includes(event.key)) return
        this.field = event.target
        this.detect()
    }

    detect() {
        const end = this.field.selectionStart
        const before = this.field.value.slice(0, end)
        const match = before.match(/(?:^|[\s(])@([^@\n]{0,100})$/u)
        if (!match) return this.close()
        const start = end - match[1].length - 1
        // A completed, selected token is no longer an open search.
        if (this.tags().some((tag) => this.field.value.slice(start).startsWith(`@${this.name(tag)}`))) return this.close()
        this.range = { start, end }
        this.queryTarget.value = match[1]
        this.show()
        this.schedule()
    }

    open() {
        if (this.field.closest("details")?.open === false || !this.field.getClientRects().length) this.field = this.bodyTarget
        this.range = { start: this.field.selectionStart, end: this.field.selectionEnd }
        this.queryTarget.value = ""
        this.show()
        this.schedule()
        this.queryTarget.focus()
    }

    show() {
        if (!this.isOpen) this.panelTarget.showPopover()
        this.field.setAttribute("aria-expanded", "true")
        this.position()
    }

    position() {
        // Mirror the textarea to place the panel beside the caret, including
        // wrapping and scroll, then clamp/flip it within the viewport.
        const rect = this.field.getBoundingClientRect()
        const style = getComputedStyle(this.field)
        const mirror = document.createElement("div")
        for (const prop of ["font", "lineHeight", "letterSpacing", "padding", "border", "boxSizing", "wordBreak", "overflowWrap"]) mirror.style[prop] = style[prop]
        Object.assign(mirror.style, { position: "fixed", top: "-10000px", left: "0", width: `${rect.width}px`, whiteSpace: "pre-wrap" })
        mirror.textContent = this.field.value.slice(0, this.range?.end ?? this.field.selectionStart)
        const marker = document.createElement("span")
        marker.textContent = "\u200b"
        mirror.append(marker)
        document.body.append(mirror)
        const x = rect.left + marker.offsetLeft - this.field.scrollLeft
        const y = Math.min(rect.bottom, rect.top + marker.offsetTop - this.field.scrollTop + (parseFloat(style.lineHeight) || 24))
        mirror.remove()
        const panel = this.panelTarget
        const width = panel.offsetWidth
        const height = panel.offsetHeight
        panel.style.left = `${Math.max(8, Math.min(x, window.innerWidth - width - 8))}px`
        const below = window.innerHeight - y - 8
        panel.style.top = `${Math.max(8, height > below && rect.top > below ? rect.top - height - 6 : Math.min(y + 6, window.innerHeight - height - 8))}px`
    }

    queryInput() { this.schedule() }

    pickKind(event) {
        this.kind = event.currentTarget.dataset.kind
        this.tabTargets.forEach((tab) => tab.setAttribute("aria-selected", String(tab.dataset.kind === this.kind)))
        this.schedule()
        this.queryTarget.focus()
    }

    schedule() {
        this.cancel()
        this.resultsTarget.replaceChildren()
        this.people = []
        this.field.removeAttribute("aria-activedescendant")
        this.queryTarget.removeAttribute("aria-activedescendant")
        const query = this.queryTarget.value.trim()
        const urlKind = query.match(/^(?:https?:\/\/)?(?:www\.)?linkedin\.com\/(company|in)\//i)
        if (urlKind) {
            this.kind = urlKind[1].toLowerCase() === "company" ? "companies" : "people"
            this.tabTargets.forEach((tab) => tab.setAttribute("aria-selected", String(tab.dataset.kind === this.kind)))
        }
        this.searchButtonTarget.textContent = /linkedin\.com\//i.test(query) ? "Look up LinkedIn URL" : "Search LinkedIn"
        this.searchButtonTarget.disabled = query.length < 2
        if (query.length < 2) return this.status("Type at least two characters, or paste a LinkedIn URL.")
        this.status("Searching suggestions…")
        this.timer = setTimeout(() => this.lookup(false), 180)
    }

    searchLinkedIn() { this.cancel(); this.lookup(true) }

    async lookup(remote) {
        const sequence = ++this.sequence
        const query = this.queryTarget.value.trim()
        this.abort = new AbortController()
        this.searchButtonTarget.disabled = true
        this.status(remote ? "Searching LinkedIn…" : "Searching suggestions…")
        const url = new URL(this.urlValue, window.location.origin)
        url.searchParams.set("q", query)
        url.searchParams.set("kind", this.kind)
        if (remote) url.searchParams.set("remote", "1")
        try {
            const response = await fetch(url, { signal: this.abort.signal, headers: { Accept: "application/json" } })
            const data = await response.json()
            if (sequence !== this.sequence || !this.isOpen) return
            this.render(data.items || [])
            this.status(data.error || (this.people.length ? (remote ? "Choose a profile to mention." : "Suggestions · Search LinkedIn for more results.") : (remote ? "No matches. Try another name or a LinkedIn URL." : "No saved matches. Search LinkedIn or paste a profile or company URL.")))
        } catch (error) {
            if (error.name !== "AbortError" && sequence === this.sequence) this.status("Couldn't load suggestions. Try again.")
        } finally {
            if (sequence === this.sequence) this.searchButtonTarget.disabled = false
        }
    }

    status(text) { this.messageTarget.textContent = text; if (this.isOpen) this.position() }

    render(people) {
        this.people = people
        this.active = 0
        this.resultsTarget.replaceChildren()
        people.forEach((person, index) => {
            const button = document.createElement("button")
            button.type = "button"
            button.className = "studio-tag-option"
            button.id = `studio_mention_option_${index}`
            button.setAttribute("role", "option")
            const name = document.createElement("span")
            name.textContent = `${person.is_company ? "🏢 " : ""}${person.name}`
            const detail = document.createElement("small")
            detail.textContent = person.detail || (person.is_company ? "Company" : "Person")
            button.append(name, detail)
            button.addEventListener("pointerdown", (event) => event.preventDefault())
            button.addEventListener("click", () => this.select(person))
            this.resultsTarget.append(button)
        })
        this.highlight()
    }

    highlight() {
        Array.from(this.resultsTarget.children).forEach((row, index) => {
            row.classList.toggle("is-active", index === this.active)
            row.setAttribute("aria-selected", String(index === this.active))
        })
        const option = this.resultsTarget.children[this.active]
        for (const field of [this.field, this.queryTarget]) {
            if (option) field.setAttribute("aria-activedescendant", option.id)
            else field.removeAttribute("aria-activedescendant")
        }
        option?.scrollIntoView({ block: "nearest" })
    }

    keydown(event) {
        if (!this.isOpen) return
        if (event.key === "Escape") {
            event.preventDefault()
            event.stopPropagation()
            this.close()
            this.field.focus()
        } else if (["ArrowDown", "ArrowUp"].includes(event.key) && this.people?.length) {
            event.preventDefault()
            this.active = (this.active + (event.key === "ArrowDown" ? 1 : -1) + this.people.length) % this.people.length
            this.highlight()
        } else if (event.key === "Enter" && this.people?.length && (event.target === this.field || event.target === this.queryTarget)) {
            event.preventDefault()
            this.select(this.people[this.active])
        } else if (event.key === "Tab" && event.target === this.field) this.close()
    }

    tags() { return Array.from(this.tagsTarget.querySelectorAll(".studio-tag")) }
    name(tag) { return tag.querySelector('[name="mentions[][name]"]').value }

    select(person) {
        const existing = this.tags().find((tag) => this.name(tag) === person.name)
        const same = existing?.dataset.profileId === String(person.profile_id) && existing?.querySelector('[name="mentions[][is_company]"]').value === String(!!person.is_company)
        if (existing && !same) return this.status("A different profile with that name is already tagged. Remove it first to change profiles.")
        if (!existing && this.tags().length >= 20) return this.status("You can mention up to 20 people or companies.")
        const { start, end } = this.range
        const before = this.field.value.slice(0, start)
        const prefix = before && !/\s$/.test(before) ? " " : ""
        const replacement = `${prefix}@${person.name} `
        if (this.field.value.length - (end - start) + replacement.length > this.field.maxLength) return this.status("This mention would exceed the text limit. Shorten the text first.")
        if (!existing) this.addChip(person)
        this.field.setRangeText(replacement, start, end, "end")
        this.close()
        this.field.focus()
        this.field.dispatchEvent(new Event("input", { bubbles: true }))
    }

    addChip(person) {
        const chip = document.createElement("span")
        chip.className = "studio-tag"
        chip.dataset.profileId = person.profile_id
        const label = document.createElement("span")
        label.textContent = `${person.is_company ? "🏢 " : "@"}${person.name}`
        chip.append(label)
        for (const [key, value] of Object.entries({ name: person.name, profile_id: person.profile_id, is_company: String(!!person.is_company) })) {
            const input = document.createElement("input")
            Object.assign(input, { type: "hidden", name: `mentions[][${key}]`, value })
            chip.append(input)
        }
        const remove = document.createElement("button")
        remove.type = "button"
        remove.className = "studio-tag-remove"
        remove.setAttribute("aria-label", `Remove ${person.name}`)
        remove.textContent = "×"
        remove.dataset.action = "studio-mentions#removeTag"
        chip.append(remove)
        this.tagsTarget.append(chip)
    }

    syncTags() {
        for (const chip of this.tags()) {
            if (!this.fields.some((field) => tokenPattern(this.name(chip)).test(field.value))) chip.remove()
        }
    }

    removeTag(event) {
        const chip = event.currentTarget.closest(".studio-tag")
        const name = this.name(chip)
        chip.remove()
        this.fields.forEach((field) => {
            field.value = field.value.replace(tokenPattern(name, "gu"), name)
            field.dispatchEvent(new Event("input", { bubbles: true }))
        })
        this.close()
    }

    cancel() {
        clearTimeout(this.timer)
        this.abort?.abort()
        this.sequence++
    }

    close() {
        this.cancel()
        if (this.isOpen) this.panelTarget.hidePopover()
        this.fields.forEach((field) => { field.setAttribute("aria-expanded", "false"); field.removeAttribute("aria-activedescendant") })
        this.queryTarget.removeAttribute("aria-activedescendant")
        this.people = []
    }
}
